import { css } from "@emotion/css";
import { colorScales } from "@octopusdeploy/design-system-tokens";
import type { AnserJsonEntry } from "anser";
import Anser from "anser";
import * as React from "react";
import { useThemePaletteType } from "../Theme/useThemePaletteType";
type AnsiColorsProps = {
    text: string;
};
export function AnsiColors(props: AnsiColorsProps) {
    const isDarkMode = useThemePaletteType() === "dark";
    return (<Ansi className={isDarkMode ? ansiClassesDark : ansiClassesLight} useClasses>
            {props.text}
        </Ansi>);
}
export const ansiClassesLight = css({
    // Foreground colors
    ".ansi-black-fg": { color: colorScales.black },
    ".ansi-red-fg": { color: colorScales.red["500"] },
    ".ansi-green-fg": { color: colorScales.green["500"] },
    ".ansi-yellow-fg": { color: colorScales.yellow["500"] },
    ".ansi-blue-fg": { color: colorScales.blue["500"] },
    ".ansi-magenta-fg": { color: colorScales.purple["500"] },
    ".ansi-cyan-fg": { color: colorScales.blue["500"] },
    ".ansi-white-fg": { color: colorScales.white },
    ".ansi-bright-black-fg": { color: colorScales.slate["900"] },
    ".ansi-bright-red-fg": { color: colorScales.red["400"] },
    ".ansi-bright-green-fg": { color: colorScales.green["400"] },
    ".ansi-bright-yellow-fg": { color: colorScales.yellow["400"] },
    ".ansi-bright-blue-fg": { color: colorScales.blue["400"] },
    ".ansi-bright-magenta-fg": { color: colorScales.purple["400"] },
    ".ansi-bright-cyan-fg": { color: colorScales.blue["400"] },
    ".ansi-bright-white-fg": { color: colorScales.navy["100"] },
    // Background colors
    ".ansi-black-bg": { backgroundColor: colorScales.slate["900"] },
    ".ansi-red-bg": { backgroundColor: colorScales.red["500"] },
    ".ansi-green-bg": { backgroundColor: colorScales.green["500"] },
    ".ansi-yellow-bg": { backgroundColor: colorScales.yellow["500"] },
    ".ansi-blue-bg": { backgroundColor: colorScales.blue["500"] },
    ".ansi-magenta-bg": { backgroundColor: colorScales.purple["500"] },
    ".ansi-cyan-bg": { backgroundColor: colorScales.blue["500"] },
    ".ansi-white-bg": { backgroundColor: colorScales.navy["200"] },
    // Code block foreground overrides (darker shades for contrast on navy["200"] background)
    "code .ansi-red-fg": { color: colorScales.red["600"] },
    "code .ansi-green-fg": { color: colorScales.green["600"] },
    "code .ansi-yellow-fg": { color: colorScales.yellow["600"] },
    "code .ansi-blue-fg": { color: colorScales.blue["600"] },
    "code .ansi-magenta-fg": { color: colorScales.purple["600"] },
    "code .ansi-cyan-fg": { color: colorScales.blue["600"] },
    "code .ansi-white-fg": { color: colorScales.white },
    "code .ansi-bright-red-fg": { color: colorScales.red["600"] },
    "code .ansi-bright-green-fg": { color: colorScales.green["600"] },
    "code .ansi-bright-yellow-fg": { color: colorScales.yellow["600"] },
    "code .ansi-bright-blue-fg": { color: colorScales.blue["600"] },
    "code .ansi-bright-magenta-fg": { color: colorScales.purple["600"] },
    "code .ansi-bright-cyan-fg": { color: colorScales.blue["600"] },
    "code .ansi-bright-white-fg": { color: colorScales.navy["100"] },
});
export const ansiClassesDark = css({
    // Foreground colors
    ".ansi-black-fg": { color: colorScales.white },
    ".ansi-red-fg": { color: colorScales.red["400"] },
    ".ansi-green-fg": { color: colorScales.green["400"] },
    ".ansi-yellow-fg": { color: colorScales.yellow["300"] },
    ".ansi-blue-fg": { color: colorScales.blue["400"] },
    ".ansi-magenta-fg": { color: colorScales.purple["400"] },
    ".ansi-cyan-fg": { color: colorScales.blue["300"] },
    ".ansi-white-fg": { color: colorScales.black },
    ".ansi-bright-black-fg": { color: colorScales.slate["200"] },
    ".ansi-bright-red-fg": { color: colorScales.red["300"] },
    ".ansi-bright-green-fg": { color: colorScales.green["300"] },
    ".ansi-bright-yellow-fg": { color: colorScales.yellow["200"] },
    ".ansi-bright-blue-fg": { color: colorScales.blue["300"] },
    ".ansi-bright-magenta-fg": { color: colorScales.purple["300"] },
    ".ansi-bright-cyan-fg": { color: colorScales.blue["200"] },
    ".ansi-bright-white-fg": { color: colorScales.slate["900"] },
    // Background colors
    ".ansi-black-bg": { backgroundColor: colorScales.white },
    ".ansi-red-bg": { backgroundColor: colorScales.red["500"] },
    ".ansi-green-bg": { backgroundColor: colorScales.green["500"] },
    ".ansi-yellow-bg": { backgroundColor: colorScales.yellow["500"] },
    ".ansi-blue-bg": { backgroundColor: colorScales.blue["500"] },
    ".ansi-magenta-bg": { backgroundColor: colorScales.purple["500"] },
    ".ansi-cyan-bg": { backgroundColor: colorScales.blue["500"] },
    ".ansi-white-bg": { backgroundColor: colorScales.slate["900"] },
    // Code block foreground overrides (lighter shades for contrast on navy["600"] background)
    "code .ansi-red-fg": { color: colorScales.red["300"] },
    "code .ansi-green-fg": { color: colorScales.green["300"] },
    "code .ansi-yellow-fg": { color: colorScales.yellow["300"] },
    "code .ansi-blue-fg": { color: colorScales.blue["300"] },
    "code .ansi-magenta-fg": { color: colorScales.purple["300"] },
    "code .ansi-cyan-fg": { color: colorScales.blue["300"] },
    "code .ansi-white-fg": { color: colorScales.black },
    "code .ansi-bright-red-fg": { color: colorScales.red["300"] },
    "code .ansi-bright-green-fg": { color: colorScales.green["300"] },
    "code .ansi-bright-yellow-fg": { color: colorScales.yellow["300"] },
    "code .ansi-bright-blue-fg": { color: colorScales.blue["300"] },
    "code .ansi-bright-magenta-fg": { color: colorScales.purple["300"] },
    "code .ansi-bright-cyan-fg": { color: colorScales.blue["300"] },
    "code .ansi-bright-white-fg": { color: colorScales.slate["900"] },
});
/**
 * MIT licenced code taken from
 * https://github.com/lgeiger/escape-carriage/blob/master/index.js
 *
 * Escape carrigage returns like a terminal
 * @param {string} txt - String to escape.
 * @return {string}    - Escaped string.
 */
export function escapeCarriageReturn(txt: string) {
    if (!txt)
        return "";
    if (!/\r/.test(txt))
        return txt;
    txt = txt.replace(/\r+\n/gm, "\n"); // \r followed by \n --> newline
    while (/\r./.test(txt)) {
        txt = txt.replace(/^([^\r\n]*)\r+([^\r\n]+)/gm, function (_, base, insert) {
            return insert + base.slice(insert.length);
        });
    }
    return txt;
}
/**
 * The remainder of this file is BSD licenced code from https://github.com/nteract/ansi-to-react/blob/master/src/index.ts
 *
 * Converts ANSI strings into JSON output.
 * @name ansiToJSON
 * @function
 * @param {string} input The input string.
 * @param {boolean} use_classes If `true`, HTML classes will be appended to the HTML output.
 * @return {Array} The parsed input.
 */
function ansiToJSON(input: string, use_classes: boolean = false): AnserJsonEntry[] {
    input = escapeCarriageReturn(input);
    return Anser.ansiToJson(input, {
        json: true,
        remove_empty: true,
        use_classes,
    });
}
export function buildAnsiClassName(entry: AnserJsonEntry): string | null {
    const parts: string[] = [];
    if (entry.fg) {
        parts.push(`${entry.fg}-fg`);
    }
    if (entry.bg) {
        parts.push(`${entry.bg}-bg`);
    }
    if (entry.decoration) {
        parts.push(`ansi-${entry.decoration}`);
    }
    return parts.length > 0 ? parts.join(" ") : null;
}
/**
 * Create the style attribute.
 * @name createStyle
 * @function
 * @param {AnserJsonEntry} bundle
 * @return {object} returns the style object
 */
function createStyle(bundle: AnserJsonEntry): React.CSSProperties {
    const style: React.CSSProperties = {};
    if (bundle.bg) {
        style.backgroundColor = `rgb(${bundle.bg})`;
    }
    if (bundle.fg) {
        style.color = `rgb(${bundle.fg})`;
    }
    switch (bundle.decoration) {
        case "bold":
            style.fontWeight = "bold";
            break;
        case "dim":
            style.opacity = "0.5";
            break;
        case "italic":
            style.fontStyle = "italic";
            break;
        case "hidden":
            style.visibility = "hidden";
            break;
        case "strikethrough":
            style.textDecoration = "line-through";
            break;
        case "underline":
            style.textDecoration = "underline";
            break;
        case "blink":
            style.textDecoration = "blink";
            break;
        default:
            break;
    }
    return style;
}
/**
 * Converts an Anser bundle into a React Node.
 * @param linkify whether links should be converting into clickable anchor tags.
 * @param useClasses should render the span with a class instead of style.
 * @param bundle Anser output.
 * @param key
 */
function convertBundleIntoReact(linkify: boolean, useClasses: boolean, bundle: AnserJsonEntry, key: number): JSX.Element {
    const style = useClasses ? null : createStyle(bundle);
    const className = useClasses ? buildAnsiClassName(bundle) : null;
    if (!linkify) {
        return React.createElement("span", { style, key, className }, bundle.content);
    }
    const content: React.ReactNode[] = [];
    const linkRegex = /(\s|^)(https?:\/\/(?:www\.|(?!www))[^\s.]+\.[^\s]{2,}|www\.[^\s]+\.[^\s]{2,})/g;
    let index = 0;
    let match: RegExpExecArray | null = null;
    while ((match = linkRegex.exec(bundle.content)) !== null) {
        const [, pre, url] = match;
        const startIndex = match.index + pre.length;
        if (startIndex > index) {
            content.push(bundle.content.substring(index, startIndex));
        }
        // Make sure the href we generate from the link is fully qualified. We assume http
        // if it starts with a www because many sites don't support https
        const href = url.startsWith("www.") ? `http://${url}` : url;
        content.push(React.createElement("a", {
            key: index,
            href,
            target: "_blank",
        }, `${url}`));
        index = linkRegex.lastIndex;
    }
    if (index < bundle.content.length) {
        content.push(bundle.content.substring(index));
    }
    return React.createElement("span", { style, key, className }, content);
}
declare interface Props {
    children?: string;
    linkify?: boolean;
    className?: string;
    useClasses?: boolean;
}
function Ansi(props: Props): JSX.Element {
    const { className, useClasses, children, linkify } = props;
    return React.createElement("span", { className }, ansiToJSON(children ?? "", useClasses ?? false).map(convertBundleIntoReact.bind(null, linkify ?? false, useClasses ?? false)));
}
